import {
  buildVoid,
  buildRefund,
  refundedTotal,
  deriveStatus,
  CorrectionError,
  type VoidRecord,
  type RefundRecord,
  type RefundableLine,
} from "../../domain/journal/corrections";
import { uuidv7 } from "../../platform/id";
import type { Money } from "../../domain/money/money";
import { coreEvents } from "../../domain/events/bus";
import {
  commitAtomically,
  getSale,
  getSaleLines,
  getVoidForSale,
  getRefundsForSale,
  getRefundedQuantities,
} from "./repositories";
import type { LocalVoid, LocalRefund, LocalRefundLine, LocalAuditEntry, SaleStatus } from "./db";

/**
 * Local void and refund writes (T157).
 *
 * Same shape as `finalize-sale.ts`, and for the same reasons:
 *
 *   1. Read the current position and build the correction — pure.
 *   2. Write the correction, its audit entry, and its outbox envelope in ONE
 *      IndexedDB transaction.
 *   3. Only then emit the event, outside the transaction, so a module
 *      subscriber cannot roll back a refund the customer has already been
 *      handed.
 *
 * NOTHING HERE AWAITS THE NETWORK. A void at 6pm during an outage is exactly as
 * final as one taken online; the envelope carries it up later. The server
 * re-checks every invariant when it arrives, because this terminal cannot see
 * refunds taken on another one (FR-055).
 *
 * NO DOCUMENT NUMBER IS CONSUMED. Corrections carry no number of their own and
 * no counter advances here — the sale's number stays exactly as issued
 * (FR-023), which is why `commitAtomically` is called without `advanceCounter`.
 */

export interface CorrectionContext {
  saleId: string;
  staffId: string;
  authorizedBy: string;
  reason: string;
}

/** What a sale currently permits, for the UI to render before anything is done. */
export interface CorrectionPosition {
  saleTotal: Money;
  isVoided: boolean;
  refundedSoFar: Money;
  status: SaleStatus;
  lines: RefundableLine[];
  canVoid: boolean;
  canRefund: boolean;
}

export async function readCorrectionPosition(saleId: string): Promise<CorrectionPosition> {
  const sale = await getSale(saleId);
  if (!sale) throw new CorrectionError(`Unknown sale ${saleId}`);

  const [existingVoid, refunds, saleLines, refundedQuantities] = await Promise.all([
    getVoidForSale(saleId),
    getRefundsForSale(saleId),
    getSaleLines(saleId),
    getRefundedQuantities(saleId),
  ]);

  const refundedSoFar = refundedTotal(refunds);
  const isVoided = existingVoid !== undefined;

  const lines: RefundableLine[] = saleLines.map((line) => ({
    saleLineId: line.id,
    quantity: line.quantity,
    lineTotal: line.lineTotal,
    refundedQuantity: refundedQuantities.get(line.id) ?? 0,
  }));

  return {
    saleTotal: sale.total,
    isVoided,
    refundedSoFar,
    // Derived, never read off the stored sale row (FR-021).
    status: deriveStatus(sale.total, isVoided, refundedSoFar),
    lines,
    canVoid: !isVoided && refundedSoFar === 0,
    canRefund: !isVoided && refundedSoFar < sale.total,
  };
}

export async function voidSale(context: CorrectionContext): Promise<VoidRecord> {
  const sale = await getSale(context.saleId);
  if (!sale) throw new CorrectionError(`Unknown sale ${context.saleId}`);

  const position = await readCorrectionPosition(context.saleId);

  const record = buildVoid({
    voidId: uuidv7(),
    saleId: context.saleId,
    saleTotal: sale.total,
    reason: context.reason,
    staffId: context.staffId,
    authorizedBy: context.authorizedBy,
    alreadyVoided: position.isVoided,
    refundedSoFar: position.refundedSoFar,
    now: new Date(),
  });

  const localVoid: LocalVoid = {
    id: record.id,
    saleId: record.saleId,
    shiftId: sale.shiftId,
    reason: record.reason,
    staffId: record.staffId,
    authorizedBy: record.authorizedBy,
    createdAt: record.createdAt,
    syncedAt: null,
  };

  const opId = uuidv7();

  await commitAtomically({
    voidRecord: localVoid,
    // FR-026. Every void is auditable at the terminal, whether or not it has
    // ever reached the server.
    auditEntry: auditFor({
      action: "sale.void",
      entityId: record.saleId,
      shiftId: sale.shiftId,
      staffId: record.staffId,
      authorizedBy: record.authorizedBy,
      reason: record.reason,
      detail: { documentNumber: sale.documentNumber, amount: sale.total },
      createdAt: record.createdAt,
    }),
    envelope: {
      opId,
      type: "sale.void",
      payload: {
        voidId: record.id,
        saleId: record.saleId,
        reason: record.reason,
        staffId: record.staffId,
        authorizedBy: record.authorizedBy,
        createdAt: record.createdAt,
      },
      createdAt: record.createdAt,
      terminalId: sale.terminalId,
      attempts: 0,
      state: "pending",
      lastError: null,
      nextAttemptAt: null,
    },
  });

  await coreEvents.emit("sale.voided", {
    opId,
    saleId: record.saleId,
    voidId: record.id,
    staffId: record.staffId,
    authorizedBy: record.authorizedBy,
    reason: record.reason,
    createdAt: record.createdAt,
  });

  return record;
}

export interface RefundRequest extends CorrectionContext {
  /** Units to return, keyed by sale line id. Zero or absent means untouched. */
  quantities: Record<string, number>;
  isCash: boolean;
}

export async function refundSale(request: RefundRequest): Promise<RefundRecord> {
  const sale = await getSale(request.saleId);
  if (!sale) throw new CorrectionError(`Unknown sale ${request.saleId}`);

  const position = await readCorrectionPosition(request.saleId);

  const record = buildRefund({
    refundId: uuidv7(),
    saleId: request.saleId,
    saleTotal: sale.total,
    reason: request.reason,
    staffId: request.staffId,
    authorizedBy: request.authorizedBy,
    isCash: request.isCash,
    refundedSoFar: position.refundedSoFar,
    alreadyVoided: position.isVoided,
    lines: position.lines.map((line) => ({
      line,
      quantity: request.quantities[line.saleLineId] ?? 0,
    })),
    now: new Date(),
  });

  const localRefund: LocalRefund = {
    id: record.id,
    saleId: record.saleId,
    shiftId: sale.shiftId,
    reason: record.reason,
    staffId: record.staffId,
    authorizedBy: record.authorizedBy,
    totalAmount: record.totalAmount,
    isCash: record.isCash,
    createdAt: record.createdAt,
    syncedAt: null,
  };

  const localLines: LocalRefundLine[] = record.lines.map((line) => ({
    id: uuidv7(),
    refundId: record.id,
    saleLineId: line.saleLineId,
    quantity: line.quantity,
    amount: line.amount,
  }));

  const opId = uuidv7();

  await commitAtomically({
    refund: localRefund,
    refundLines: localLines,
    auditEntry: auditFor({
      action: "sale.refund",
      entityId: record.saleId,
      shiftId: sale.shiftId,
      staffId: record.staffId,
      authorizedBy: record.authorizedBy,
      reason: record.reason,
      detail: {
        documentNumber: sale.documentNumber,
        amount: record.totalAmount,
        isCash: record.isCash,
      },
      createdAt: record.createdAt,
    }),
    envelope: {
      opId,
      type: "sale.refund",
      payload: {
        refundId: record.id,
        saleId: record.saleId,
        reason: record.reason,
        staffId: record.staffId,
        authorizedBy: record.authorizedBy,
        totalAmount: record.totalAmount,
        isCash: record.isCash,
        createdAt: record.createdAt,
        lines: localLines.map((line) => ({
          id: line.id,
          saleLineId: line.saleLineId,
          quantity: line.quantity,
          amount: line.amount,
        })),
      },
      createdAt: record.createdAt,
      terminalId: sale.terminalId,
      attempts: 0,
      state: "pending",
      lastError: null,
      nextAttemptAt: null,
    },
  });

  // The drawer module listens for this and kicks on a cash refund — one of the
  // three permitted triggers. A non-cash refund must not open the drawer.
  await coreEvents.emit("sale.refunded", {
    opId,
    saleId: record.saleId,
    refundId: record.id,
    staffId: record.staffId,
    authorizedBy: record.authorizedBy,
    hasCashRefund: record.isCash,
    lines: record.lines.map((line) => ({
      saleLineId: line.saleLineId,
      quantity: line.quantity,
    })),
    createdAt: record.createdAt,
  });

  return record;
}

function auditFor(input: Omit<LocalAuditEntry, "id" | "entityType">): LocalAuditEntry {
  return {
    id: uuidv7(),
    entityType: "sale",
    ...input,
  };
}

export { CorrectionError };
export type { RefundableLine };
